import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { Stack, Typography } from "@mui/material";
import SearchTermsGrid from "../data-grid/search-terms-grid";
import TopOpportunities from "../top-opportunities";
import ClicksAdCostsChart from "../clicks-ad-costs-chart";
import VisibilityChart from "../visibility-chart";
import TopCompetitors from "../top-competitors";
import RegionSelector from "../region-selector";
import NonBrandedProvider from "../context/use-non-branded";
import useLocales from "src/locales/use-locales";
import '../_style.scss';
import { setFilterCompare, setFilterDate, setFilterDateCompare, setFilterTimestep } from "src/context/reducers/filter-settings/filterSlice";
import { fDate, getLast12WeeksDateRange } from "src/utils/format-time";
import { SplashScreen } from "src/components/loading-screen";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { subDays } from "date-fns";
import { useRegionsQuery } from "src/context/api/non-branded/api";
import { RootState } from "src/context/store";



export default function SearchTerms() {
    const ref = useRef<boolean>(false);
    const { t } = useLocales();
    const dispatch = useDispatch();
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [selectedOpportunity, setSelectedOpportunity] = useState<string | null>(null);
    const filterStore = useSelector((state: RootState) => state.filters);
    const workspace = useCurrentWorkspace()!;

    const { data: regionsData, isLoading, isFetching, isUninitialized } = useRegionsQuery({
        workspace_id: workspace?.id as number,
        start_date: fDate(filterStore.start, "yyyy-MM-dd"),
        end_date: fDate(filterStore.end, "yyyy-MM-dd"),
        devices: filterStore.devices,
        countries: selectedRegion ? [selectedRegion] : [],
        platforms: filterStore.platforms,
        search_terms: filterStore.keywords,
    }, { skip: !workspace?.id });

    useEffect(() => {
        const { start, end } = getLast12WeeksDateRange();
        dispatch(setFilterDate({ start, end }));
        dispatch(setFilterCompare(false))
        dispatch(setFilterTimestep("year_week"))


        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
        ref.current = true;
    }, []);

    useEffect(() => {
        if (filterStore?.countries?.length > 0) {
            setSelectedRegion(filterStore?.countries[0]);
        }
    }, [filterStore?.countries]);

    if (!ref.current) return <SplashScreen />;

    const renderFooter = (title: string) => {
        return (
            <Stack className="card-footer" width="100%">
                <Typography className="card-footer-text" variant="caption" color="text.secondary" textAlign="center">
                    {title}
                </Typography>
            </Stack>
        );
    };
    return (
        <NonBrandedProvider values={{ selectedRegion, setSelectedRegion, selectedOpportunity, setSelectedOpportunity }}>
            <Stack direction={"column"} sx={{fontFamily: 'Public Sans'}} spacing={2} px={"24px"}>
                <RegionSelector data={regionsData?.data || []} loading={isLoading || isFetching || isUninitialized} />
                <Stack
                    direction="row"
                    spacing={2}
                    px={"24px"}
                >
                    <Stack className="card-top-header-container" overflow="hidden" width="100%" direction="column" maxHeight={224} height={224} borderRadius={2} p={0} pb={0} px={0} bgcolor="white" sx={{ border: '1px solid rgba(145, 158, 171, 0.12)' }}>
                        <ClicksAdCostsChart />
                        {renderFooter(t("dashboard.nonBrand.last12Weeks"))}
                    </Stack>
                    <Stack className="card-top-header-container" overflow="hidden" width="100%" maxHeight={224} height={224} borderRadius={2} p={0} pb={0} px={0} bgcolor="white" sx={{ border: '1px solid rgba(145, 158, 171, 0.12)' }}>
                        <VisibilityChart />

                        {renderFooter(t("dashboard.nonBrand.last12Weeks"))}
                    </Stack>
                    <Stack className="card-top-header-container" overflow="hidden" width="100%" maxHeight={224} height={224} borderRadius={2} p={0} pb={0} px={0} bgcolor="white" sx={{ border: '1px solid rgba(145, 158, 171, 0.12)' }}>
                        <TopCompetitors />
                        {renderFooter(t("dashboard.nonBrand.last12Weeks"))}
                    </Stack>
                </Stack>
                <Stack px={"24px"}>
                    <Stack borderRadius={2} m={0} bgcolor="white" sx={{ border: '1px solid rgba(145, 158, 171, 0.12)' }}>
                        <TopOpportunities />
                    </Stack>
                </Stack>
                <Stack px={"24px"}>
                    <SearchTermsGrid />
                </Stack>
            </Stack>
        </NonBrandedProvider>
    );
}